/**
 * useAdminResources Hooks (T076, T077 - US3)
 * React Query hooks for resource content management with optimistic updates
 * 
 * Implements:
 * - useAdminResources: List resources with pagination and comprehensive filters
 * - useAdminResource: Get single resource by ID
 * - useCreateResource: Create resource mutation
 * - useUpdateResource: Update resource mutation
 * - useDeleteResource: Delete resource mutation
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { adminResourcesApi } from '../services/adminResourcesApi';
import type { PaginatedResponse } from '../types/pagination.type';
import type {
  AdminResource,
  CreateResourcePayload,
  UpdateResourcePayload,
  ResourceListParams,
  ResourceType,
} from '../types/admin-resource.type';

// Simple category type for hook
type CategoryOption = {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
};

// Query keys for cache management
export const resourceKeys = {
  all: ['admin', 'resources'] as const,
  lists: () => [...resourceKeys.all, 'list'] as const,
  list: (params: ResourceListParams) => [...resourceKeys.lists(), params] as const,
  details: () => [...resourceKeys.all, 'detail'] as const,
  detail: (id: string) => [...resourceKeys.details(), id] as const,
  types: () => [...resourceKeys.all, 'types'] as const,
  categories: () => [...resourceKeys.all, 'categories'] as const,
};

/**
 * Hook to fetch paginated resources list
 * 
 * @param params - Query parameters (page, pageSize, search, comprehensive filters)
 * @returns Query result with paginated resources
 * 
 * Features:
 * - 30s stale time to reduce API calls
 * - keepPreviousData for smooth pagination
 * - Total count reflects filtered results
 * - Comprehensive filters: status, type, category, tags, featured flags, author, publish date
 * 
 * @example
 * const { data, isLoading, error } = useAdminResources({
 *   page: 1,
 *   pageSize: 50,
 *   search: 'medicare',
 *   status: 'published',
 *   typeId: 'type-1',
 *   isFeatured: true
 * });
 */
export function useAdminResources(
  params: ResourceListParams = {},
): UseQueryResult<PaginatedResponse<AdminResource>, Error> {
  return useQuery({
    queryKey: resourceKeys.list(params),
    queryFn: () => adminResourcesApi.listResources(params),
    staleTime: 30000, // 30 seconds
    placeholderData: (previousData) => previousData, // Keep previous data during refetch
  });
}

/**
 * Hook to fetch single resource by ID
 * 
 * @param id - Resource ID
 * @returns Query result with resource details
 */
export function useAdminResource(
  id: string,
): UseQueryResult<AdminResource, Error> {
  return useQuery({
    queryKey: resourceKeys.detail(id),
    queryFn: () => adminResourcesApi.getResource(id),
    enabled: !!id,
  });
}

/**
 * Hook to create new resource
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Invalidates resources list on success
 * - Returns created resource immediately
 * 
 * @example
 * const createResource = useCreateResource();
 * 
 * await createResource.mutateAsync({
 *   title: 'Understanding Medicare',
 *   content: '<p>Content here...</p>',
 *   seoUrl: 'understanding-medicare',
 *   typeId: 'type-1',
 *   categoryIds: ['cat-1'],
 *   tagIds: ['tag-1']
 * });
 */
export function useCreateResource(): UseMutationResult<
  AdminResource,
  Error,
  CreateResourcePayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    // Wrapped so React Query's second mutationFn argument is not forwarded on.
    mutationFn: (payload: CreateResourcePayload) =>
      adminResourcesApi.createResource(payload),
    onSuccess: () => {
      // Invalidate all resource lists to refetch with new resource
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
    },
  });
}

/**
 * Hook to update existing resource
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Optimistic update (immediate UI update)
 * - Rollback on error
 * - Updates cache with new data
 * 
 * @example
 * const updateResource = useUpdateResource();
 * 
 * await updateResource.mutateAsync({
 *   id: 'resource_123',
 *   payload: { title: 'Updated Title', status: 'published' }
 * });
 */
export function useUpdateResource(): UseMutationResult<
  AdminResource,
  Error,
  { id: string; payload: UpdateResourcePayload }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => adminResourcesApi.updateResource(id, payload),
    
    // Optimistic update
    onMutate: async ({ id, payload }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: resourceKeys.detail(id) });

      // Snapshot previous value
      const previousResource = queryClient.getQueryData<AdminResource>(
        resourceKeys.detail(id),
      );

      // Optimistically update cache
      if (previousResource) {
        queryClient.setQueryData<AdminResource>(resourceKeys.detail(id), {
          ...previousResource,
          ...payload,
        });
      }

      return { previousResource };
    },
    
    // Rollback on error
    onError: (_error, { id }, context) => {
      if (context?.previousResource) {
        queryClient.setQueryData(resourceKeys.detail(id), context.previousResource);
      }
    },
    
    // Refetch on success
    onSuccess: (data, { id }) => {
      // Update detail cache with server response
      queryClient.setQueryData(resourceKeys.detail(id), data);
      
      // Invalidate lists to show updated resource
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
    },
  });
}

/**
 * Hook to soft-delete resource
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Optimistic removal from lists
 * - Rollback on error
 * - Invalidates all related queries
 * 
 * @example
 * const deleteResource = useDeleteResource();
 * 
 * await deleteResource.mutateAsync('resource_123');
 */
export function useDeleteResource(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminResourcesApi.softDeleteResource(id),
    
    // Optimistic update - remove from lists
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: resourceKeys.lists() });

      // Snapshot all list queries
      const previousLists = queryClient.getQueriesData<
        PaginatedResponse<AdminResource>
      >({
        queryKey: resourceKeys.lists(),
      });

      // Optimistically remove from all cached lists
      queryClient.setQueriesData<PaginatedResponse<AdminResource>>(
        { queryKey: resourceKeys.lists() },
        (old) => {
          if (!old) return old;
          
          return {
            ...old,
            items: old.items.filter((resource) => resource.id !== id),
            meta: {
              ...old.meta,
              total: old.meta.total - 1,
            },
          };
        },
      );

      return { previousLists };
    },
    
    // Rollback on error
    onError: (_error, _id, context) => {
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    
    // Refetch on success
    onSuccess: (_data, id) => {
      // Remove detail cache
      queryClient.removeQueries({ queryKey: resourceKeys.detail(id) });
      
      // Invalidate all lists to get fresh totals
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
    },
  });
}

/**
 * Hook to fetch resource types for dropdowns
 * 
 * @returns Query result with resource types
 * 
 * Features:
 * - Long stale time (5 minutes) since types rarely change
 * - Cached for reuse across components
 * 
 * @example
 * const { data: types, isLoading } = useResourceTypes();
 */
export function useResourceTypes(): UseQueryResult<ResourceType[], Error> {
  return useQuery({
    queryKey: resourceKeys.types(),
    queryFn: adminResourcesApi.getResourceTypes,
    staleTime: 300000, // 5 minutes - types rarely change
  });
}

/**
 * Hook to fetch resource categories for dropdowns
 * 
 * @returns Query result with categories
 * 
 * Features:
 * - Long stale time (5 minutes) since categories rarely change
 * - Cached for reuse across components
 * 
 * @example
 * const { data: categories, isLoading } = useResourceCategories();
 */
export function useResourceCategories(): UseQueryResult<CategoryOption[], Error> {
  return useQuery({
    queryKey: resourceKeys.categories(),
    queryFn: adminResourcesApi.getResourceCategories,
    staleTime: 300000, // 5 minutes - categories rarely change
  });
}
